// Import model
import { OtgCarpetCatalogStyle } from '../models/OtgCarpetCatalogStyle';

// Import base class serializer
import { OtgCatalogStyleSerializer } from './OtgCatalogStyleSerializer';

// Import dependency types (models and enums used in properties)
import { SearchMetaData } from '../models/SearchMetaData';

// Import dependency serializers (for complex types)
import { SearchMetaDataSerializer } from './SearchMetaDataSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for OtgCarpetCatalogStyle - handles JSON serialization and deserialization with type validation
 */
export class OtgCarpetCatalogStyleSerializer {
  /**
   * Deserializes a JSON string into a OtgCarpetCatalogStyle instance
   */
  static deserialize(json: string): OtgCarpetCatalogStyle {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a OtgCarpetCatalogStyle instance
   */
  static fromObject(obj: any): OtgCarpetCatalogStyle {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to OtgCarpetCatalogStyle');
    }


    const instance: OtgCarpetCatalogStyle = {
      styleNumber: obj['styleNumber'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      materialType: obj['materialType'] as string,
      links: obj['links'] as string | undefined,
      collectionName: obj['collectionName'] as string | undefined,
      crossoverName: obj['crossoverName'] as string | undefined,
      vendor: obj['vendor'] as string | undefined,
      carpetType: obj['carpetType'] as string | undefined,
      color: obj['color'] as string | undefined,
      demo: obj['demo'] as string | undefined,
      width: obj['width'] as string | undefined,
      patternMatch: obj['patternMatch'] as string | undefined,
      fiber: obj['fiber'] as string | undefined,
      durabilityRating: obj['durabilityRating'] as number | undefined,
      stainResistanceRating: obj['stainResistanceRating'] as number | undefined,
      priceGroup: obj['priceGroup'] as number | undefined,
      competitionCrushers: obj['competitionCrushers'] as boolean | undefined,
      unitCost: obj['unitCost'] as string | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      id: obj['_id'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      searchMetaData: obj['searchMetaData'] ? SearchMetaDataSerializer.fromObject(obj['searchMetaData']) : undefined as any,
      catalogFeedId: obj['catalogFeedId'] as string | undefined,
      catalogFeedIdName: obj['catalogFeedId|Name'] as string | undefined,
      manufacturerId: obj['manufacturerId'] as string | undefined,
      manufacturerIdName: obj['manufacturerId|Name'] as string | undefined,
      count: obj['Count'] as number | undefined,
      propertiesPlankLength: obj['Properties|PlankLength'] as string | undefined,
      propertiesTexture: obj['Properties|Texture'] as string | undefined,
      propertiesThickness: obj['Properties|Thickness'] as string | undefined,
      propertiesWoodSpecies: obj['Properties|WoodSpecies'] as string | undefined,
      propertiesWoodType: obj['Properties|WoodType'] as string | undefined,
      propertiesInstallationMethod: obj['Properties|InstallationMethod'] as string | undefined,
      propertiesLaminatesType: obj['Properties|LaminatesType'] as string | undefined,
      propertiesLength: obj['Properties|Length'] as string | undefined,
      propertiesResilientType: obj['Properties|ResilientType'] as string | undefined,
      propertiesWearLayer: obj['Properties|WearLayer'] as string | undefined,
      _656Ff204: obj['_656ff204'] as any | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of OtgCarpetCatalogStyle instances
   */
  static deserializeArray(json: string): OtgCarpetCatalogStyle[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a OtgCarpetCatalogStyle instance to a JSON string
   */
  static serialize(instance: OtgCarpetCatalogStyle): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a OtgCarpetCatalogStyle instance to a plain object
   */
  static toObject(instance: OtgCarpetCatalogStyle): any {
    return {
      'styleNumber': instance.styleNumber,
      'name': instance.name,
      'description': instance.description,
      'materialType': instance.materialType,
      'links': instance.links,
      'collectionName': instance.collectionName,
      'crossoverName': instance.crossoverName,
      'vendor': instance.vendor,
      'carpetType': instance.carpetType,
      'color': instance.color,
      'demo': instance.demo,
      'width': instance.width,
      'patternMatch': instance.patternMatch,
      'fiber': instance.fiber,
      'durabilityRating': instance.durabilityRating,
      'stainResistanceRating': instance.stainResistanceRating,
      'priceGroup': instance.priceGroup,
      'competitionCrushers': instance.competitionCrushers,
      'unitCost': instance.unitCost,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      '_id': instance.id,
      'isActive': instance.isActive,
      'searchMetaData': instance.searchMetaData ? SearchMetaDataSerializer.toObject(instance.searchMetaData) : undefined,
      'catalogFeedId': instance.catalogFeedId,
      'catalogFeedId|Name': instance.catalogFeedIdName,
      'manufacturerId': instance.manufacturerId,
      'manufacturerId|Name': instance.manufacturerIdName,
      'Count': instance.count,
      'Properties|PlankLength': instance.propertiesPlankLength,
      'Properties|Texture': instance.propertiesTexture,
      'Properties|Thickness': instance.propertiesThickness,
      'Properties|WoodSpecies': instance.propertiesWoodSpecies,
      'Properties|WoodType': instance.propertiesWoodType,
      'Properties|InstallationMethod': instance.propertiesInstallationMethod,
      'Properties|LaminatesType': instance.propertiesLaminatesType,
      'Properties|Length': instance.propertiesLength,
      'Properties|ResilientType': instance.propertiesResilientType,
      'Properties|WearLayer': instance.propertiesWearLayer,
      '_656ff204': instance._656Ff204,
    };
  }

  /**
   * Serializes an array of OtgCarpetCatalogStyle instances to a JSON string
   */
  static serializeArray(instances: OtgCarpetCatalogStyle[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
